import clsx from 'clsx';
import { useState, useEffect, memo } from 'react';
import { useParams } from 'react-router';

import Header from '../../components/Header';
import Loader from '../../components/Loader';
import PlayerData from '../../components/PlayerData';
import { useTopScorerStore } from '../../store';
import { DataHandler } from '../../utils/DataHandler';
import { getPlayerIdByArray, getPlayerInfoFromId, getKind } from '../../utils/misc';
import type { PlayerResponse } from '../../utils/types';

import GoogleTagManagerHelper from '@tracking/gtm-helper';

import * as styles from './styles.module.scss';

const PlayerDetail = ({ options, fromMain = true }) => {
  const { playerId } = useParams();
  const playersInfo = useTopScorerStore((state) => state.playersInfo);
  const editorialInfo = useTopScorerStore((state) => state.editorialInfo);
  const loadPlayersInfo = useTopScorerStore((state) => state.loadPlayersInfo);
  const loadEditorialInfo = useTopScorerStore((state) => state.loadEditorialInfo);

  const [plData, setPlayerData] = useState<PlayerResponse>(null);
  const [goalData, setGoalData] = useState({});
  const [loadingPlayer, setLoadingPlayer] = useState(true);
  const { sponsor } = options;

  useEffect(() => {
    loadPlayersInfo();
  }, [loadPlayersInfo]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const apiInstance = DataHandler.getInstance();

    const updateData = async () => {
      const pData = apiInstance && (await apiInstance.loadSinglePlayerInfo(playerId));
      const gData = apiInstance && (await apiInstance.loadMatchEvents(playerId));
      setPlayerData(pData?.[0]);
      setGoalData(gData);
      setLoadingPlayer(false);
      playersInfo && loadEditorialInfo(getPlayerIdByArray(playersInfo));
    };
    updateData();
  }, [loadEditorialInfo, playerId, playersInfo]);

  useEffect(() => {
    const playerName = plData?.player?.internationalName;
    if (!playerName) return;

    const data = {
      pageType: 'player',
      pageSubType: 'topscorer',
      pageName: `/${window.competitionUrl}/topscorer/playerdetail/${playerId}-${playerName}`,
      virtPath: `/${window.competitionUrl}/topscorer/playerdetail/${playerId}/`,
      competition: GoogleTagManagerHelper.getFromDataLayer('competition'),
      competitionDate: GoogleTagManagerHelper.getFromDataLayer('competitionDate'),
      language: GoogleTagManagerHelper.getFromDataLayer('language'),
      country: GoogleTagManagerHelper.getFromDataLayer('country'),
      pageFormat: GoogleTagManagerHelper.getFromDataLayer('pageFormat'),
      pageTitle: GoogleTagManagerHelper.getFromDataLayer('pageTitle'),
      contentSection1: GoogleTagManagerHelper.getFromDataLayer('competition'),
      contentSection2: 'topscorer',
      contentSection3: 'playerdetail',
      partners: sponsor,
      pageLanguage: GoogleTagManagerHelper.getFromDataLayer('language'),
    };
    GoogleTagManagerHelper.addPageMeta(data);
    GoogleTagManagerHelper.addVirtualPage(data);
  }, [playerId, sponsor, plData]);

  const kind = plData?.player && getKind(plData, editorialInfo);
  const playerInfo = getPlayerInfoFromId(playersInfo, playerId);

  return (
    <div
      className={clsx(styles['ts-player-wrap'], styles[`ts-player--${kind}`], `ts-player--${kind}`, 'pk-theme--light')}
    >
      {plData && plData.player && (
        <Header playerData={plData} editorialInfo={editorialInfo} kind={kind} fromMain={fromMain} />
      )}
      {loadingPlayer && <Loader />}
      <PlayerData playerData={plData} playerInfo={playerInfo} options={options} goalData={goalData} />
    </div>
  );
};

export default memo(PlayerDetail);
